import React from "react";
import {
  CircularProgress,
  Link,
  useMediaQuery,
  useTheme,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Grade as GradeIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import {
  bindTrigger,
  bindMenu,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { parsePhoneNumber } from "awesome-phonenumber";
import Copy from "../../../icons/Copy";
import { ErrorTooltip } from "../../../layout/ErrorTooltip";
import { getRtkErrorMessage } from "../../../../lib/utils/rtkErrorHelper";
import {
  CommunicationTypeEnum,
  CustomerCommunication,
  useDeleteUsersCommunicationsMutation,
  usePostUsersCommunicationsMutation,
} from "../../../../store/services/communicationService";
import {
  CustomerInvoicingDeliveryType,
  useGetCustomerByIdQuery,
} from "../../../../store/services/customerService";
import AlertDialog from "../../../dialogs/AlertDialog";

interface ActivityPopupStateProps {
  customerNo: number;
  communication: CustomerCommunication;
  setEditing: (edit: CustomerCommunication | undefined) => void;
  isCompany?: boolean;
}

const copyToClipboard = async (value: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(value);
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
  }
};

export default function ActivityPopupState({
  customerNo,
  communication,
  setEditing,
  isCompany = false,
}: ActivityPopupStateProps): React.JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: customer } = useGetCustomerByIdQuery(customerNo);

  const isPhoneType =
    communication.Type === CommunicationTypeEnum.Telefon ||
    communication.Type === CommunicationTypeEnum.Mobiltelefon;

  const [deleteConfirmDialogState, setDeleteConfirmDialogState] =
    React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const [deleteCommunication, { isLoading: isDeleting, error: errorDeleting }] =
    useDeleteUsersCommunicationsMutation();

  const [updateCommunication, { isLoading: isUpdating }] =
    usePostUsersCommunicationsMutation();

  const popupState = usePopupState({
    variant: "popover",
    popupId: "activityPopupState",
  });

  const handleError = (error: unknown) => {
    const message =
      error instanceof Error ? error.message : "Ett oväntat fel inträffade";
    setErrorMessage(message);
  };

  const handleRemove = async () => {
    try {
      await deleteCommunication({
        customerNo,
        customerCommunication: [communication],
      }).unwrap();
    } catch (err) {
      handleError(err);
    }
  };

  const handleMakePrimary = async () => {
    try {
      await updateCommunication({
        customerNo,
        customerCommunication: [{ ...communication, IsPrimary: true }],
      }).unwrap();
    } catch (err) {
      handleError(err);
    }
  };

  const handleToggleOnlyInvoice = async () => {
    try {
      await updateCommunication({
        customerNo,
        customerCommunication: [
          {
            ...communication,
            IsNoExternal: !communication.IsNoExternal,
          },
        ],
      }).unwrap();
    } catch (err) {
      handleError(err);
    }
  };

  const isEmailRemovalDisabled = React.useMemo(() => {
    return (
      communication.Invoice &&
      customer?.CustomerInvoicingDelivery?.some(
        (x) => x.Selected && x.Id === CustomerInvoicingDeliveryType.EmailInvoice
      )
    );
  }, [communication.Invoice, customer?.CustomerInvoicingDelivery]);

  const formattedCommunicationValue = React.useMemo(() => {
    const phone = parsePhoneNumber(
      (communication.Value ?? "").replace(/^0{2}/, "+").replace(/^0/, "+46")
    );

    const isValidPhone =
      (communication.Type === CommunicationTypeEnum.Telefon && phone.valid) ||
      (communication.Type === CommunicationTypeEnum.Mobiltelefon &&
        phone.typeIsMobile);

    if (!isValidPhone) return communication.Value;

    return phone.countryCode === 46
      ? phone.number?.national
      : phone.number?.international;
  }, [communication.Value, communication.Type]);

  return (
    <>
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <ErrorTooltip
          title={errorDeleting ? getRtkErrorMessage(errorDeleting) : ""}
          arrow
        >
          <Link
            href="#"
            {...bindTrigger(popupState)}
            sx={{
              color: theme.palette.text.primary,
              textDecoration:
                communication.IsNoExternal && communication.IsPrimary
                  ? "line-through"
                  : "none",
            }}
          >
            {(isUpdating || isDeleting) && (
              <CircularProgress size={15} color="success" sx={{ mr: 1 }} />
            )}
            {formattedCommunicationValue}
          </Link>
        </ErrorTooltip>

        <Menu {...bindMenu(popupState)}>
          {isMobile && isPhoneType && communication.Value && (
            <MenuItem
              component="a"
              href={`tel:${communication.Value.replace(/[^\d+]/g, "")}`}
              onClick={popupState.close}
              dense
            >
              <ListItemIcon sx={{ minWidth: 35 }}>
                <PhoneIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">Ring upp</Typography>
            </MenuItem>
          )}

          <MenuItem
            onClick={async () => {
              await copyToClipboard(communication.Value ?? "");
              popupState.close();
            }}
            dense
          >
            <ListItemIcon sx={{ minWidth: 35 }}>
              <Copy />
            </ListItemIcon>
            <Typography variant="inherit">Kopiera</Typography>
          </MenuItem>

          {!isCompany && (
            <MenuItem
              onClick={() => {
                setEditing(communication);
                popupState.close();
              }}
              dense
            >
              <ListItemIcon sx={{ minWidth: 35 }}>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">Redigera</Typography>
            </MenuItem>
          )}

          {!communication.IsPrimary && !isCompany && (
            <MenuItem
              onClick={() => {
                handleMakePrimary();
                popupState.close();
              }}
              dense
            >
              <ListItemIcon sx={{ minWidth: 35 }}>
                <GradeIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">Gör Primär</Typography>
            </MenuItem>
          )}

          {communication.Type === CommunicationTypeEnum.Epost &&
            communication.IsPrimary && (
              <MenuItem
                onClick={() => {
                  handleToggleOnlyInvoice();
                  popupState.close();
                }}
                dense
              >
                <ListItemIcon sx={{ minWidth: 35 }}>
                  <DescriptionIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">
                  {communication.IsNoExternal
                    ? "Ta bort endast faktura"
                    : "Endast faktura"}
                </Typography>
              </MenuItem>
            )}

          {!isCompany && (
            <MenuItem
              onClick={() => {
                if (!communication.IsPrimary) {
                  handleRemove();
                } else {
                  setDeleteConfirmDialogState(true);
                }
                popupState.close();
              }}
              dense
              disabled={isEmailRemovalDisabled}
            >
              <ListItemIcon sx={{ minWidth: 35 }}>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">Ta bort</Typography>
            </MenuItem>
          )}

          <MenuItem onClick={popupState.close} dense>
            <ListItemIcon sx={{ minWidth: 35 }}>
              <CloseIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Stäng</Typography>
          </MenuItem>
        </Menu>
      </div>

      <AlertDialog
        Message="Bekräfta borttagning av primär kommunikationstyp."
        IsOpen={deleteConfirmDialogState}
        HandleClose={() => setDeleteConfirmDialogState(false)}
        HandleAccept={() => {
          handleRemove();
          setDeleteConfirmDialogState(false);
        }}
        NoBtnText="Avbryt"
        YesBtnText="Ta bort"
        Title="Primär kommunikationstyp"
      />

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
